import React from 'react';
import axios from 'axios';
import qs from 'qs';
import { withSnackbar } from 'notistack';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import AnnouncementComment from './AnnouncementComment';
import confirmDialog from '../../shared/ConfirmDialog';
import textareaDialog from '../../shared/TextareaDialog';

const styles = () => ({
  root: {
    marginTop: 20,
  },
  newCommentWrapper: {
    marginBottom: 30,
  },
  newCommentButtonWrapper: {
    textAlign: 'right',
  },
  disclaimer: {
    color: '#999999',
    textAlign: 'left',
    paddingRight: 20,
    marginBottom: 10,
  },
  showAllCommentsWrapper: {
    textAlign: 'center',
  },
  marginTop: {
    marginTop: 10,
  },
});

const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
  };
};

class AnnouncementComments extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isLoaded: false,
      data: [],
      meta: {},
      commentText: '',
      submitting: false,
      showAllComments: false,
    };

    this.reloadData = this.reloadData.bind(this);
    this.onAddCommentButtonClick = this.onAddCommentButtonClick.bind(this);
    this.onEditClick = this.onEditClick.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.onShowAllCommentsClick = this.onShowAllCommentsClick.bind(this);
  }

  componentDidMount() {
    this.reloadData();
  }

  componentWillUnmount() {
    if (this.axiosCancel) {
      this.axiosCancel();
    }
  }

  reloadData() {
    const {
      announcement,
      selectedPupil,
    } = this.props;

    if (
      !selectedPupil ||
      announcement.allow_comments === 'no'
    ) {
      return;
    }

    this.setState({
      isLoading: true,
    });

    axios
      .get(
        `${window.apiUrl}/announcementcomments/${announcement.id}?pupil_id=${selectedPupil.id}`,
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          const { data, meta } = response.data;

          this.setState({
            isLoading: false,
            isLoaded: true,
            data,
            meta,
          });
        }
      }).catch((thrown) => {});
  }

  onAddCommentButtonClick() {
    const {
      t,
      enqueueSnackbar,
      announcement,
      selectedPupil,
    } = this.props;
    const {
      commentText,
    } = this.state;

    this.setState({
      submitting: true,
    });

    axios
      .post(
        `${window.apiUrl}/addannouncementcomment/${announcement.id}`,
        qs.stringify({
          pupil_id: selectedPupil.id,
          text: commentText,
        }),
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            commentText: '',
          });

          this.reloadData();

          enqueueSnackbar(t('You have successfully added the comment.'), {
            variant: 'success',
            autoHideDuration: 2000,
          });
        } else {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        }

        this.setState({
          submitting: false,
        });
      }).catch((thrown) => {});
  }

  onEditClick(entry, event) {
    const {
      t,
      enqueueSnackbar,
    } = this.props;

    event.preventDefault();

    textareaDialog(t('Please edit the text below'), {
      defaultValue: entry.text,
    }).then(
      (text) => {
        axios
          .post(
            `${window.apiUrl}/editannouncementcomment/${entry.id}`,
            qs.stringify({
              text,
            }),
            {
              cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
            }
          ).then((response) => {
            if (response.data.error) {
              enqueueSnackbar(response.data.error, {
                variant: 'error',
                autoHideDuration: 2000,
              });
            } else {
              enqueueSnackbar(t('You have successfully edited the comment'), {
                variant: 'success',
                autoHideDuration: 2000,
              });

              this.reloadData();
            }
          }).catch((thrown) => {});
      },
      () => {
        // Nothing to do
      }
    );
  }

  onDeleteClick(entry, event) {
    const {
      t,
      enqueueSnackbar,
    } = this.props;

    event.preventDefault();

    confirmDialog(t('Are you sure you wish to remove this comment?')).then(
      () => {
        axios
          .post(
            `${window.apiUrl}/deleteannouncementcomment/${entry.id}`,
            {},
            {
              cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
            }
          ).then((response) => {
            if (response.data.error) {
              enqueueSnackbar(response.data.error, {
                variant: 'error',
                autoHideDuration: 2000,
              });
            } else {
              enqueueSnackbar(t('You have successfully deleted the comment'), {
                variant: 'success',
                autoHideDuration: 2000,
              });

              this.reloadData();
            }
          }).catch((thrown) => {});
      },
      () => {
        // Nothing to do
      }
    );
  }

  onShowAllCommentsClick(event) {
    const {
      showAllComments,
    } = this.state;

    event.preventDefault();

    this.setState({
      showAllComments: !showAllComments,
    });
  }

  render() {
    const {
      classes,
      t,
      announcement,
      selectedPupil,
    } = this.props;
    const {
      data,
      isLoaded,
      isLoading,
      commentText,
      showAllComments,
    } = this.state;

    if (
      !selectedPupil ||
      announcement.allow_comments === 'no'
    ) {
      return null;
    }

    const comments = showAllComments
      ? data
      : data.slice(0, 3);

    return (
      <div className={`announcement-comments ${classes.root}`}>
        <div className={classes.newCommentWrapper}>
          <TextField
            label={t('Comment')}
            value={commentText}
            onChange={(event) => {
              this.setState({
                commentText: event.target.value,
              });
            }}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
          />
          <Grid
            container
            justify="space-between"
            className={classes.marginTop}
          >
            {announcement.comment_visibility !== 'none' && (
              <Grid item className={classes.disclaimer}>
                {announcement.comment_visibility === 'private' && t('Comments will be private and only viewable by school staff.')}
                {announcement.comment_visibility === 'public' && t('Comments will be viewable by all parents/carers with accounts associated with this pupil, and school staff.')}
              </Grid>
            )}
            <Grid item className={classes.newCommentButtonWrapper}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={this.onAddCommentButtonClick}
                className="add-comment-button"
              >
                {t('Add comment')}
              </Button>
            </Grid>
          </Grid>
        </div>
        {isLoading && !isLoaded && (
          <CircularProgress />
        )}
        {isLoaded && data.length === 0 && (
          <p>{t('There are currently no comments for this announcement.')}</p>
        )}
        {isLoaded && data.length > 0 && (
          <>
            {comments.map((entry) => {
              return (
                <AnnouncementComment
                  key={entry.id}
                  data={entry}
                  onDeleteLinkClick={this.onDeleteClick.bind(this, entry)}
                  onEditLinkClick={this.onEditClick.bind(this, entry)}
                />
              )
            })}
            {data.length > 3 && (
              <div className={classes.showAllCommentsWrapper}>
                {!showAllComments && (
                  <a href="#" onClick={this.onShowAllCommentsClick}>{t('Show all comments')}</a>
                )}
                {showAllComments && (
                  <a href="#" onClick={this.onShowAllCommentsClick}>{t('Hide comments')}</a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps)(
  withStyles(styles)(withSnackbar(withTranslation()(AnnouncementComments)))
);
